import React, { useState, useEffect } from 'react';
import changelog from '../data/changelog.json';
import { useAuth } from '../contexts/AuthContext';
import { v3 } from '../lib/styleTokens';

const STORAGE_KEY = 'ferrum_changelog_last_seen';

// v3.5.99.7.16: extended to cover all 4 audience values actually used in the
// data (was previously only 'all' and 'admin'). 'internal' and 'developer'
// entries were rendering with no badge under the old map.
const AUDIENCE_LABEL = { all: 'All Users', admin: 'Admins', internal: 'Internal', developer: 'Developer' };
const AUDIENCE_COLOR = { all: '#166534', admin: '#1e40af', internal: '#92400e', developer: '#5b21b6' };
const AUDIENCE_BG    = { all: '#dcfce7', admin: '#dbeafe', internal: '#fef3c7', developer: '#ede9fe' };

const CAT_CONFIG = {
  new:         { label: 'New',         color: '#166534', bg: '#dcfce7', icon: '✦' },
  improved:    { label: 'Improved',    color: '#0f766e', bg: '#d1fae5', icon: '↑' },
  fixed:       { label: 'Fixed',       color: '#1e40af', bg: '#dbeafe', icon: '✓' },
  coming_soon: { label: 'Coming Soon', color: '#7c3aed', bg: '#ede9fe', icon: '◆' },
};

const DEFAULT_CAT = { color: '#475569', bg: '#f1f5f9', icon: '•' };
const catFor = (key) => CAT_CONFIG[key] || { ...DEFAULT_CAT, label: key };

// v3.5.99.7.16: Derive a short headline from the summary when an entry
// doesn't have an explicit `title` field. Handles the long historical tail
// of entries that were written before the title/summary split was formalized.
// Strategy: take the first sentence (up to first period/em-dash/colon at a
// reasonable break point), then cap at ~80 chars. Strips common prefixes
// like "Hotfix:", "Fix:" so the headline reads as a clean noun phrase.
function deriveTitle(entry) {
  if (entry.title) return entry.title;
  let s = (entry.summary || '').trim();
  if (!s) return 'Update';
  // Cut at first em-dash, period, or colon — whichever comes first within
  // the first 120 chars. Beyond 120 chars we just hard-truncate.
  const cutPoints = ['—', '. ', ': '];
  let cutAt = -1;
  for (const cp of cutPoints) {
    const ix = s.indexOf(cp);
    if (ix > 0 && ix < 120 && (cutAt < 0 || ix < cutAt)) cutAt = ix;
  }
  if (cutAt > 0) s = s.slice(0, cutAt);
  // Strip leading "Hotfix:", "Fix:" etc.
  s = s.replace(/^(hotfix|fix|fixes|chore|note|adds?|updates?|removes?)[:\s-]+/i, '');
  // Capitalize first letter
  if (s.length) s = s[0].toUpperCase() + s.slice(1);
  // Hard cap
  if (s.length > 90) s = s.slice(0, 87).trim() + '…';
  return s;
}


export default function ChangelogPage() {
  const { isAdmin } = useAuth();
  const [lastSeen, setLastSeen] = useState(null);
  const [expanded, setExpanded] = useState(new Set([changelog[0]?.version]));

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setLastSeen(stored);
    localStorage.setItem(STORAGE_KEY, changelog[0]?.version);
  }, []);

  function toggleExpand(version) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(version) ? next.delete(version) : next.add(version);
      return next;
    });
  }

  const unseenCount = lastSeen
    ? changelog.filter(r => r.version > lastSeen).length
    : changelog.length;

  // v3.5.99.7.16: non-admins see entries marked 'all' only. 'admin', 'internal',
  // and 'developer' are all gated to admins. Prior behavior gated only 'admin',
  // which leaked internal/developer entries to all users.
  const visible = changelog.filter(r => isAdmin || r.audience === 'all');

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '20px 16px', overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, fontFamily: v3.font.family }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <h1 style={{ fontSize: v3.font.size.pageTitle, fontWeight: v3.font.weight.semibold, color: v3.color.body, margin: 0, letterSpacing: v3.font.letterSpacing.tight }}>What's New</h1>
          {unseenCount > 0 && (
            <span style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.bold, background: v3.color.ink, color: 'white', padding: '2px 8px', borderRadius: v3.radius.pill }}>
              {unseenCount} new
            </span>
          )}
        </div>
        <p style={{ fontSize: v3.font.size.label, color: v3.color.muted, margin: 0 }}>
          Release history for the FerrumIT Pricing Platform. Click a release to see what changed.
        </p>
      </div>

      {/* Timeline */}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 16, top: 0, bottom: 0, width: 2, background: '#e5e7eb', zIndex: 0 }}/>

        {visible.map((release, i) => {
          const isNew      = lastSeen && release.version > lastSeen;
          const isOpen     = expanded.has(release.version);
          const totalItems = Object.values(release.categories).flat().length;
          const hasContent = totalItems > 0;
          const title      = deriveTitle(release);
          const hasSummary = !!release.summary && release.summary.trim().length > 0;
          const dateLabel  = new Date(release.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

          return (
            <div key={release.version} style={{ position: 'relative', paddingLeft: 44, marginBottom: 12 }}>
              {/* Dot */}
              <div style={{ position: 'absolute', left: 8, top: 18, width: 18, height: 18, borderRadius: '50%', background: i === 0 ? '#2563eb' : '#e5e7eb', border: `3px solid ${i === 0 ? '#2563eb' : '#d1d5db'}`, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {i === 0 && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }}/>}
              </div>

              {/* Card */}
              <div style={{ background: v3.color.card, borderRadius: v3.radius.cardLarge, border: `1px solid ${isNew ? v3.color.inkBorder : v3.color.border}`, overflow: 'hidden', boxShadow: isNew ? `0 0 0 3px ${v3.color.inkBg}` : 'none' }}>
                {/* Card header — title prominent, meta secondary */}
                <div onClick={() => (hasContent || hasSummary) && toggleExpand(release.version)}
                  style={{ padding: '12px 16px', cursor: (hasContent || hasSummary) ? 'pointer' : 'default', background: i === 0 ? '#f8faff' : 'white' }}>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    {/* Left rail: version stack */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, flexShrink: 0, minWidth: 86 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'DM Mono, monospace', color: '#0f1e3c', background: '#f1f5f9', padding: '2px 7px', borderRadius: 4 }}>
                        v{release.version}
                      </span>
                      {isNew && (
                        <span style={{ fontSize: 9, fontWeight: 700, background: '#2563eb', color: 'white', padding: '1px 5px', borderRadius: 8 }}>NEW</span>
                      )}
                    </div>

                    {/* Center: title + meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: v3.font.size.header, fontWeight: v3.font.weight.semibold, color: v3.color.body, lineHeight: 1.35, marginBottom: 4 }}>
                        {title}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>{dateLabel}</span>
                        {AUDIENCE_LABEL[release.audience] && (
                          <span style={{ fontSize: 9, fontWeight: 600, background: AUDIENCE_BG[release.audience] || '#f1f5f9', color: AUDIENCE_COLOR[release.audience] || '#475569', padding: '1px 5px', borderRadius: 3 }}>
                            {AUDIENCE_LABEL[release.audience]}
                          </span>
                        )}
                        {Object.entries(release.categories).map(([key, items]) => {
                          if (!items.length) return null;
                          const cfg = catFor(key);
                          return (
                            <span key={key} style={{ fontSize: 9, fontWeight: 700, background: cfg.bg, color: cfg.color, padding: '1px 6px', borderRadius: 3 }}>
                              {cfg.icon} {items.length} {cfg.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {(hasContent || hasSummary) && (
                      <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0, marginTop: 2 }}>{isOpen ? '▲' : '▼'}</span>
                    )}
                  </div>
                </div>

                {/* Expanded details — summary first, then per-category bullets */}
                {isOpen && (hasContent || hasSummary) && (
                  <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                    {hasSummary && (
                      <p style={{ margin: '12px 0 0 0', fontSize: v3.font.size.body, lineHeight: 1.55, color: v3.color.bodySoft }}>
                        {release.summary}
                      </p>
                    )}
                    {Object.entries(release.categories).map(([key, items]) => {
                      if (!items.length) return null;
                      const cfg = catFor(key);
                      return (
                        <div key={key} style={{ marginTop: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', background: cfg.bg, color: cfg.color, padding: '2px 7px', borderRadius: 3 }}>
                              {cfg.icon} {cfg.label}
                            </span>
                          </div>
                          <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'none' }}>
                            {items.map((item, idx) => (
                              <li key={idx} style={{ fontSize: v3.font.size.body, color: v3.color.bodySoft, lineHeight: 1.55, marginBottom: 4, display: 'flex', gap: 6 }}>
                                <span style={{ color: cfg.color, flexShrink: 0, fontSize: 10, marginTop: 3 }}>•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 11 }}>
        FerrumIT Pricing Platform · Built by FerrumIT with Anthropic Claude
      </div>
    </div>
  );
}
